import { useTranslation } from "@refinedev/core";
import { HTMLAttributes } from "react";
import { FaUser, FaPhone, FaChevronRight } from "react-icons/fa6";

export interface HelpProfessionalCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
    id: string;
    description?: string;
    name?: string;
    specialty?: string[];
    photo?: string;
    email?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    country?: {
        code: string;
        name: string;
    };
    active?: boolean;
    onContact?: () => void;
    contactActive?: boolean;
}

const HelpProfessionalCard = ({
    id,
    name,
    email,
    description,
    specialty,
    photo,
    className,
    onChange,
    active,
    onContact,
    contactActive,
    country,
    ...rest
}: HelpProfessionalCardProps) => {
    const { translate } = useTranslation();

    return (
        <div
            className={`collapse bg-base-100 shadow-md hover:shadow-lg transition-all duration-200 border border-base-200 rounded-lg cursor-pointer ${active ? 'ring-2 ring-secondary collapse-open' : ''} ${className ?? ''}`}
            {...rest}
        >
            <input
                type="radio"
                name="professional-accordion"
                checked={active}
                onChange={() => onChange && onChange({} as any)}
            />
            <div className="collapse-title flex items-center gap-4">
                <div className="avatar flex-shrink-0">
                    <div className="w-16 h-16 rounded-full">
                        {photo ? (
                            <img
                                src={photo}
                                alt={name || "Professional"}
                                className="w-full h-full object-cover rounded-full"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = "/img/profile_avatar_placeholder_large.png";
                                }}
                            />
                        ) : (
                            <div className="w-full h-full bg-base-300 rounded-full flex items-center justify-center">
                                <FaUser className="w-8 h-8 text-base-content/50" />
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex-1 min-w-0">
                    {/* Fix max with of this text, in Mobile occupy more that container width */}
                    <h2 className="text-lg font-bold text-base-content leading-tight text-clip mb-3">
                        {name?.slice(0, 40) || translate("page.helps.unknownProfessional")}
                    </h2>
                    <div className="flex flex-wrap gap-1 mt-1">
                        <span className="badge badge-ghost text-xs truncate">{translate(`pro-defs.${specialty?.[0]}`)}</span>
                    </div>
                    {country && <div className={`flex items-center text-sm gap-2 mt-3 ${active ? '' : 'hidden'}`}>
                        <span className={`fi fi-${country.code.toLowerCase()}`}></span>
                        <span>{country.name}</span>
                    </div>}
                </div>

                <div className="flex-shrink-0 ml-2">
                    <FaChevronRight className={`w-4 h-4 text-base-content/30 transition-transform duration-200 ${active ? 'rotate-90' : ''}`} />
                </div>
            </div>


            <div className="collapse-content w-full border-t border-base-200 space-y-4">
                <div className="p-4 space-y-4">
                    {description && (
                        <div>
                            <h3 className="font-semibold text-sm mb-1 text-base-content/70">
                                {translate("page.helps.about")}
                            </h3>
                            <p className="text-sm text-base-content leading-relaxed">
                                {description}
                            </p>
                        </div>
                    )}

                    {specialty && specialty.length > 1 && (
                        <div>
                            <h3 className="font-semibold text-sm mb-1 text-base-content/70">
                                {translate("page.helps.specialties")}
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {specialty.slice(1).map((s, i) => (
                                    <span key={i} className="badge badge-warning">{translate(`pro-defs.${s}`)}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        className={`btn btn-secondary w-full gap-2`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onContact) onContact();
                        }}
                        disabled={!contactActive}
                    >
                        <FaPhone className="w-4 h-4" />
                        {contactActive ? translate("page.helps.contact-now") : translate("common.loading")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HelpProfessionalCard;